/**
 * WHAT Ctrl+P PRODUCES.
 *
 * The PDF export is the deliberate artefact — it carries the cover block, the
 * caveats and the rasterised charts. But people print screens, and until now
 * printing any of the three screens this replaces produced the same thing: the
 * sidebar, the notification bell, a filter rail, two skeleton panels, and every
 * chart clipped at the fold because nothing told the page where it may break.
 *
 * THE FOUR RULES
 *
 *  1. THE CHROME GOES. A printed page has no navigation, no notification dock
 *     and no buttons. Everything that only exists to change what is on screen is
 *     `display: none` — including the export menu itself, which on paper is a
 *     list of options nobody can click.
 *
 *  2. THE CAVEATS STAY. Every panel note, the scope sentence, the enforcement
 *     warning and the truncation banner print. A sheet of paper carrying the
 *     figures and not the sentence saying they cover the whole organisation is
 *     the same dangerous artefact as an export that drops it — and paper is
 *     harder to correct afterwards.
 *
 *  3. NOTHING BREAKS MID-PANEL. `break-inside: avoid` on every panel, so a chart
 *     and its own caption never land on different sheets. A chart on page 2 with
 *     its caveat on page 3 is a chart that will be read without its caveat.
 *
 *  4. INK IS NOT COLOUR. `print-color-adjust: exact` keeps the series colours,
 *     because a stacked bar printed as five identical greys is not a chart. The
 *     page background is forced white regardless, so a dark surface does not
 *     empty a toner cartridge.
 *
 * There is no dark mode in this app, so there is no dark-mode print case.
 */
import React from 'react';
import { Global, css } from '@emotion/react';
import styled from '@emotion/styled';
import { palette } from '../../../../style-utils/theme';

export const PRINT_HIDDEN = 'dash-no-print';

const rules = css`
  @media print {
    /* ── 1. the chrome ── */
    aside,
    nav,
    [data-testid='bell-dock'],
    [data-testid='tab-bar'],
    [data-testid='filter-rail'],
    [data-testid='filter-sheet'],
    [data-testid='export-menu'],
    [data-testid='export-button'],
    [data-testid='open-filters'],
    [data-testid='iq-launcher'],
    .${PRINT_HIDDEN} {
      display: none !important;
    }

    /* A chart's own controls are chrome too — a Chart/Table toggle on paper is
       a button that does nothing, sitting where a legend could have been. */
    [data-testid^='panel-'] button {
      display: none !important;
    }

    /* ── 2. the page ── */
    @page {
      margin: 14mm;
    }

    html,
    body {
      background: ${palette.white} !important;
      width: 100%;
      margin: 0;
      padding: 0;
    }

    body * {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    /* The app shell is a fixed-height flex column with its own scroller. On
       paper there is no scroller, and a fixed height crops the document to one
       screenful — which is what the three old screens printed. */
    #root,
    #root > div,
    main {
      display: block !important;
      height: auto !important;
      max-height: none !important;
      overflow: visible !important;
      position: static !important;
    }

    /* ── 3. the panels ── */
    [data-testid^='panel-'] {
      break-inside: avoid;
      page-break-inside: avoid;
      box-shadow: none !important;
      border: 1px solid ${palette.line} !important;
      width: 100% !important;
      max-width: 100% !important;
    }

    /* A panel's notes are the reason the panel is not misleading. They print at
       full contrast rather than the muted grey they carry on screen. */
    [data-testid='panel-notes'],
    [data-testid='scope-caption'],
    [data-testid='scope-warning'],
    [data-testid='truncation-banner'] {
      color: ${palette.ink} !important;
      break-inside: avoid;
    }

    /* Two panels per row wastes half the paper on a portrait sheet; the grid
       collapses to one column and each panel gets its full width. */
    [data-testid='panel-grid'] {
      display: block !important;
    }

    /* A table that scrolls sideways on screen prints every column. */
    [data-testid='panel-table'],
    [data-testid='records-table'] {
      overflow: visible !important;
      width: 100% !important;
    }

    thead {
      display: table-header-group;
    }
    tr {
      break-inside: avoid;
      page-break-inside: avoid;
    }

  }
`;

/** Mounted once by the Dashboard. Global, because what has to change for
 *  print lives above this component — the app shell, the sidebar, the bell. */
export const PrintRules: React.FC = () => <Global styles={rules} />;

/* ── the address the reader cannot see on paper ──
   Every filter on this screen is in the URL, so a printed sheet that carries
   the URL is a sheet somebody can reproduce exactly — including the person who
   was handed it in a meeting and wants to check it themselves. */
const SourceLine = styled.span`
  display: none;

  @media print {
    display: block;
    font-size: 9px;
    color: ${palette.slate};
    word-break: break-all;
  }
`;

/** The URL, printed. Invisible on screen. */
export const PrintSource: React.FC<{ url: string }> = ({ url }) => (
  <SourceLine data-testid="print-source">{url}</SourceLine>
);
